import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Container, Grid, TextField, Select, MenuItem, FormControl,
    InputLabel, Button, Typography, Box, Chip, Alert, Avatar,
    Skeleton, CircularProgress,
} from '@mui/material';
import { debounce } from 'lodash';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Surface, DirectionChip, PerformanceValue, ConflictBadge } from '../components/primitives';

const API_BASE = 'https://api.capitolgains.io';
const CURRENT_YEAR = new Date().getFullYear();
const MotionBox = motion(Box);

// ── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    });
};

const formatValue = (n) => {
    if (n == null) return '—';
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
    return `$${n.toLocaleString()}`;
};

const normalizeChamber = (body) => {
    if (!body) return '';
    const b = body.toUpperCase();
    if (b === 'H' || b === 'HOUSE') return 'House';
    if (b === 'S' || b === 'SENATE') return 'Senate';
    return body;
};

// ── URL state helpers ──────────────────────────────────────────────────────
//
// The URL is the source of truth for filter state. These helpers translate
// between URL params and the shape the fetch + UI consume.

const readFiltersFromURL = (searchParams) => ({
    // Year is intentionally NOT defaulted to the current year — the default
    // view is "all trades newest first," which lets the backend use the
    // by_date GSI. Year is opt-in via the filter input.
    year:        searchParams.get('year') || '',
    chamber:     searchParams.get('chamber') || '',
    asset_code:  searchParams.get('asset_code') || '',
    transaction: searchParams.get('transaction') || '',
    ticker:      searchParams.get('ticker') || '',
    member_id:   searchParams.get('member_id') || '',
    name:        searchParams.get('name') || '',
});

const buildQuery = (filters, cursor) => {
    const qs = new URLSearchParams();
    // ticker > member_id > year — most specific partition wins on the server.
    if (filters.ticker)         qs.set('ticker', filters.ticker);
    else if (filters.member_id) qs.set('member_id', filters.member_id);
    else if (filters.year)      qs.set('year', filters.year);

    if (filters.chamber)        qs.set('chamber', filters.chamber);
    if (filters.asset_code)     qs.set('asset_code', filters.asset_code);
    if (filters.transaction)    qs.set('transaction', filters.transaction);

    const parts = (filters.name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) qs.set('last_name', parts[0]);
    else if (parts.length >= 2) {
        qs.set('first_name', parts[0]);
        qs.set('last_name', parts.slice(1).join(' '));
    }

    if (cursor) qs.set('cursor', cursor);
    return qs.toString();
};

// ── Trade Card ─────────────────────────────────────────────────────────────

function TradeCard({ trade, index }) {
    const roiValue = trade.stock_roi_1yr != null ? (trade.stock_roi_1yr - 1) * 100 : null;
    const isOption = trade.asset_code === 'OP';
    const memberName = `${trade.first_name || ''} ${trade.last_name || ''}`.trim();

    const assetLabel = trade.asset_name
        ? trade.asset_name.replace(/\b\w/g, c => c.toUpperCase()).slice(0, 55) + (trade.asset_name.length > 55 ? '…' : '')
        : '';

    return (
        <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: Math.min(index, 20) * 0.03 }}
            sx={{ height: '100%' }}
        >
            <Surface
                padding={0}
                interactive
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 1.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Avatar
                            src={trade.ticker ? `https://img.logo.dev/ticker/${trade.ticker}?token=pk_Fy4ZV5RMQEKzNJfSy9h6BA` : undefined}
                            alt={trade.ticker}
                            sx={{ width: 36, height: 36, bgcolor: '#fff', flexShrink: 0 }}
                        />
                        <Box minWidth={0}>
                            {trade.ticker ? (
                                <Typography
                                    component={Link}
                                    to={`/issuers/${encodeURIComponent(trade.ticker)}`}
                                    sx={{
                                        fontFamily: t => t.typography.fontFamilyMono,
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        lineHeight: 1.2,
                                        letterSpacing: '-0.01em',
                                        textDecoration: 'none',
                                        '&:hover': { color: 'primary.light' },
                                    }}
                                >
                                    {trade.ticker}
                                </Typography>
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: t => t.typography.fontFamilyMono,
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        lineHeight: 1.2,
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    —
                                </Typography>
                            )}
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.muted', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {assetLabel}
                            </Typography>
                            {trade.sector && (
                                <Chip
                                    label={trade.sector}
                                    size="small"
                                    sx={{
                                        mt: 0.5,
                                        height: 18,
                                        fontSize: '0.65rem',
                                        bgcolor: 'custom.surface.elevated',
                                        color: 'text.secondary',
                                        border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                    <DirectionChip direction={trade.transaction} />
                </Box>

                {/* Body */}
                <Box sx={{ px: 2.5, py: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                    {/* Member + date */}
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                        <Box minWidth={0}>
                            {trade.member_id ? (
                                <Typography
                                    component={Link}
                                    to={`/congressmember/${trade.member_id}`}
                                    variant="body2"
                                    sx={{ color: 'text.link', fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    {memberName}
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                    {memberName}
                                </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: 'text.muted', display: 'block' }}>
                                {normalizeChamber(trade.governing_body)}{isOption ? ' · Option' : ' · Stock'}
                            </Typography>
                            {trade.conflicts && trade.conflicts.length > 0 && (
                                <Box sx={{ mt: 0.75 }}>
                                    <ConflictBadge conflicts={trade.conflicts} />
                                </Box>
                            )}
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.muted', flexShrink: 0, fontFamily: t => t.typography.fontFamilyMono }}
                        >
                            {formatDate(trade.transaction_date)}
                        </Typography>
                    </Box>

                    {/* Value + price */}
                    {(trade.asset_value_low != null || trade.stock_price != null) && (
                        <Box display="flex" gap={3}>
                            {trade.asset_value_low != null && trade.asset_value_high != null && (
                                <Box>
                                    <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>
                                        Value
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: t => t.typography.fontFamilyMono }}>
                                        {formatValue(trade.asset_value_low)}–{formatValue(trade.asset_value_high)}
                                    </Typography>
                                </Box>
                            )}
                            {trade.stock_price != null && (
                                <Box>
                                    <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>
                                        Price
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: t => t.typography.fontFamilyMono }}>
                                        ${Number(trade.stock_price).toFixed(2)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Options details */}
                    {isOption && (
                        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                            {trade.option_type && (
                                <Chip label={trade.option_type.toUpperCase()} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                            )}
                            {trade.strike_price != null && (
                                <Typography variant="caption" sx={{ color: 'text.muted', fontFamily: t => t.typography.fontFamilyMono }}>
                                    Strike ${trade.strike_price.toFixed(2)}
                                </Typography>
                            )}
                            {trade.option_exp_date && (
                                <Typography variant="caption" sx={{ color: 'text.muted', fontFamily: t => t.typography.fontFamilyMono }}>
                                    Exp {formatDate(trade.option_exp_date)}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* ROI pinned to bottom */}
                    <Box sx={{ mt: 'auto', pt: 1 }}>
                        {roiValue != null ? (
                            <Box sx={{
                                px: 1.5, py: 1,
                                borderRadius: 1,
                                bgcolor: 'custom.surface.subtle',
                                border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}>
                                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                                    1-Year ROI
                                </Typography>
                                <PerformanceValue value={roiValue} variant="body2" />
                            </Box>
                        ) : isOption && trade.option_roi != null ? (
                            <Box sx={{
                                px: 1.5, py: 1,
                                borderRadius: 1,
                                bgcolor: 'custom.surface.subtle',
                                border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}>
                                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                                    Option ROI
                                </Typography>
                                <PerformanceValue value={trade.option_roi * 100} variant="body2" />
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Surface>
        </MotionBox>
    );
}

// ── Page ───────────────────────────────────────────────────────────────────

function RecentTrades() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = readFiltersFromURL(searchParams);

    // Local state for the name input only — debounced URL writes prevent the
    // address bar from churning on every keystroke. All other filters sync to
    // the URL immediately.
    const [nameInput, setNameInput] = useState(filters.name);

    // If the URL changes externally (back/forward, deep link, clear button),
    // re-sync the local name input.
    useEffect(() => { setNameInput(filters.name); }, [filters.name]);

    const [trades, setTrades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);

    // Generation counter: incremented on every filter change. Both the
    // first-page fetch and loadMore tag themselves with the gen at start and
    // discard their data on resolve if the gen no longer matches. Cleanly
    // handles two race classes:
    //   1. fast filter changes (newer first-page fetch supersedes older)
    //   2. filter changes during pagination (in-flight loadMore drops its
    //      append rather than mixing old-filter rows into new-filter results)
    const fetchGenRef = useRef(0);

    // ── Fetch effect — driven entirely by URL state ───────────────────────
    //
    // Re-runs whenever any URL param changes. Always fetches the FIRST page
    // (cursor=null). Pagination is a separate code path (loadMore below).

    useEffect(() => {
        const myGen = ++fetchGenRef.current;
        setLoading(true);
        setError(null);
        const qs = buildQuery(filters, null);
        fetch(`${API_BASE}/v1/trades?${qs}`)
            .then(async (res) => {
                if (!res.ok) {
                    const errBody = await res.json().catch(() => ({}));
                    throw new Error(errBody?.error?.message || 'Failed to fetch trades');
                }
                return res.json();
            })
            .then((envelope) => {
                if (myGen !== fetchGenRef.current) return;
                setTrades(envelope.data || []);
                setNextCursor(envelope.meta?.next_cursor || null);
            })
            .catch((err) => {
                if (myGen === fetchGenRef.current) setError(err);
            })
            .finally(() => {
                // Only the latest fetch effect owns the loading flag; older
                // ones leave it alone so they don't override the new one's
                // setLoading(true).
                if (myGen === fetchGenRef.current) setLoading(false);
            });
        // filters is derived from searchParams — that's the real dep.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // ── Pagination ────────────────────────────────────────────────────────

    const loadMore = useCallback(async () => {
        if (!nextCursor || loadingMore || loading) return;
        // Capture generation at start; if it changes mid-flight (filter
        // changed under us), we drop the appended data on resolve.
        const myGen = fetchGenRef.current;
        setLoadingMore(true);
        try {
            const qs = buildQuery(filters, nextCursor);
            const res = await fetch(`${API_BASE}/v1/trades?${qs}`);
            if (!res.ok) throw new Error('Failed to load more');
            const envelope = await res.json();
            if (myGen !== fetchGenRef.current) return;
            setTrades((prev) => [...prev, ...(envelope.data || [])]);
            setNextCursor(envelope.meta?.next_cursor || null);
        } catch (err) {
            if (myGen === fetchGenRef.current) setError(err);
        } finally {
            // Always clear the loading-more UI flag, even on stale resolves —
            // otherwise a stale loadMore would leave the spinner stuck on.
            setLoadingMore(false);
        }
        // filters is derived from searchParams — that's the real dep.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextCursor, loadingMore, loading, searchParams]);

    // ── IntersectionObserver-driven infinite scroll ───────────────────────

    const sentinelRef = useRef(null);

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !nextCursor) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMore();
            },
            { rootMargin: '400px' }, // start loading well before the sentinel hits the viewport
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [nextCursor, loadMore]);

    // ── Filter mutators — write straight to URL ───────────────────────────

    const updateFilter = (name, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(name, value);
        else next.delete(name);
        setSearchParams(next, { replace: true });
    };

    const handleFilterChange = (e) => {
        updateFilter(e.target.name, e.target.value);
    };

    // Name search: debounced URL write. Local input updates immediately so
    // typing feels responsive; URL (and the fetch) catches up after 400ms.
    //
    // Critical: the useMemo dep is just [setSearchParams] (which is stable per
    // react-router-dom v6). Including `searchParams` would re-create the
    // debounced function on every URL change, defeating the whole point of
    // debouncing. The closure uses the functional setSearchParams(prev => ...)
    // form so it always reads the current URL state when it fires.
    const debouncedSetName = useMemo(
        () => debounce((value) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (value) next.set('name', value);
                else next.delete('name');
                return next;
            }, { replace: true });
        }, 400),
        [setSearchParams],
    );

    const handleNameChange = (e) => {
        const value = e.target.value;
        setNameInput(value);
        debouncedSetName(value);
    };

    const handleClearFilters = () => {
        debouncedSetName.cancel();
        setNameInput('');
        setSearchParams(new URLSearchParams(), { replace: true });
    };

    // ── Active filters (for chip display) ─────────────────────────────────

    const activeFilters = [
        filters.chamber     && { key: 'chamber',     label: `Chamber: ${filters.chamber}` },
        filters.asset_code  && { key: 'asset_code',  label: `Asset: ${filters.asset_code === 'ST' ? 'Stocks' : 'Options'}` },
        filters.transaction && { key: 'transaction', label: `Type: ${filters.transaction}` },
        filters.ticker      && { key: 'ticker',      label: `Ticker: ${filters.ticker}` },
        filters.member_id   && { key: 'member_id',   label: `Member: ${filters.member_id}` },
        filters.name        && { key: 'name',        label: `Name: ${filters.name}` },
        filters.year        && { key: 'year',        label: `Year: ${filters.year}` },
    ].filter(Boolean);

    const hasActiveFilters = activeFilters.length > 0;

    return (
        <Container sx={{ pt: 6, pb: 8 }}>

            {/* Page Header */}
            <Typography variant="h1" sx={{ color: 'text.primary', mb: 0.5 }}>
                Recent Congressional Trades
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Stock and options disclosures filed by members of Congress.
            </Typography>

            {/* Filters */}
            <Surface padding={2.5} sx={{ mb: 2 }}>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="Member Name"
                            placeholder="e.g. Pelosi or Nancy Pelosi"
                            value={nameInput}
                            onChange={handleNameChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            name="year"
                            label="Year"
                            type="number"
                            value={filters.year}
                            onChange={handleFilterChange}
                            fullWidth
                            inputProps={{ min: 2000, max: CURRENT_YEAR }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Chamber</InputLabel>
                            <Select name="chamber" value={filters.chamber} onChange={handleFilterChange} label="Chamber">
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="HOUSE">House</MenuItem>
                                <MenuItem value="SENATE">Senate</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Asset</InputLabel>
                            <Select name="asset_code" value={filters.asset_code} onChange={handleFilterChange} label="Asset">
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="ST">Stocks</MenuItem>
                                <MenuItem value="OP">Options</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Type</InputLabel>
                            <Select name="transaction" value={filters.transaction} onChange={handleFilterChange} label="Type">
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="purchase">Buy</MenuItem>
                                <MenuItem value="sale">Sell</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Surface>

            {/* Active-filter chips — only shown when something non-default is set */}
            {hasActiveFilters && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2, alignItems: 'center' }}>
                    {activeFilters.map((f) => (
                        <Chip
                            key={f.key}
                            label={f.label}
                            size="small"
                            onDelete={() => updateFilter(f.key, '')}
                            sx={{ height: 24 }}
                        />
                    ))}
                    <Button size="small" onClick={handleClearFilters} sx={{ ml: 0.5 }}>
                        Clear all
                    </Button>
                </Box>
            )}

            {/* Results */}
            {loading ? (
                <Grid container spacing={2}>
                    {[...Array(6)].map((_, i) => (
                        <Grid item xs={12} md={6} key={i}>
                            <Surface padding={0} sx={{ overflow: 'hidden' }}>
                                <Box sx={{ p: 2.5, borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Skeleton variant="circular" width={36} height={36} />
                                    <Box flex={1}>
                                        <Skeleton width="30%" height={18} />
                                        <Skeleton width="60%" height={14} sx={{ mt: 0.5 }} />
                                    </Box>
                                    <Skeleton variant="rounded" width={44} height={20} />
                                </Box>
                                <Box sx={{ p: 2.5 }}>
                                    <Skeleton width="50%" height={16} />
                                    <Skeleton width="35%" height={12} sx={{ mt: 0.5 }} />
                                    <Skeleton width="80%" height={16} sx={{ mt: 2 }} />
                                    <Skeleton variant="rounded" width="100%" height={36} sx={{ mt: 2 }} />
                                </Box>
                            </Surface>
                        </Grid>
                    ))}
                </Grid>
            ) : error ? (
                <Alert severity="error">
                    Failed to load trades — {error.message}
                </Alert>
            ) : trades.length === 0 ? (
                <Alert
                    severity="info"
                    action={hasActiveFilters && (
                        <Button color="inherit" size="small" onClick={handleClearFilters}>
                            Clear filters
                        </Button>
                    )}
                >
                    No trades match your filters.
                </Alert>
            ) : (
                <>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                        Showing <strong style={{ color: 'inherit' }}>{trades.length.toLocaleString()}</strong> trade{trades.length !== 1 ? 's' : ''}
                        {hasActiveFilters ? ' matching your filters' : ''}
                        {nextCursor ? ' · scroll for more' : ''}
                    </Typography>
                    <Grid container spacing={2}>
                        {trades.map((trade, index) => (
                            <Grid item xs={12} md={6} key={`${trade.trade_id || trade.doc_id || index}-${index}`}>
                                <TradeCard trade={trade} index={index} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Sentinel — IntersectionObserver triggers loadMore() when this scrolls into view */}
                    <Box
                        ref={sentinelRef}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 3,
                            minHeight: 60,
                        }}
                    >
                        {loadingMore && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CircularProgress size={18} />
                                <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                    Loading more…
                                </Typography>
                            </Box>
                        )}
                        {!nextCursor && !loadingMore && trades.length > 0 && (
                            <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                End of results.
                            </Typography>
                        )}
                    </Box>
                </>
            )}
        </Container>
    );
}

export default RecentTrades;
